import { ApiError, apiErrorReason } from '@/lib/api';

import type { CatalogEntity, ProcessArea } from './types';
import { errorAdvice } from '@/lib/error-advice';

// Presentación pura de Configuración › Catálogos: textos legibles, validación de
// campos con la misma regla del API (el API sigue siendo la autoridad) y mensajes
// accionables para cada rechazo (UX-COPY-01, UX-ERR-01).

const FRAGMENT_MAX = 8;
const FREQUENCY_MAX = 3650;

function unit(n: number, one: string, many: string): string {
  return n === 1 ? `Cada ${one}` : `Cada ${n} ${many}`;
}

/** "Cada año", "Cada 6 meses", "Cada 45 días": la unidad más grande exacta. */
export function reviewFrequencyLabel(days: number): string {
  if (days % 365 === 0) {
    return unit(days / 365, 'año', 'años');
  }
  if (days % 30 === 0) {
    return unit(days / 30, 'mes', 'meses');
  }
  return unit(days, 'día', 'días');
}

export function codeFragmentError(raw: string): string | null {
  const value = raw.trim().toUpperCase();
  if (value === '') {
    return 'Escribe el fragmento de código';
  }
  if (value.length > FRAGMENT_MAX) {
    return `El fragmento admite hasta ${FRAGMENT_MAX} caracteres`;
  }
  return /^[A-Z0-9]+$/.test(value) ? null : 'Usa solo letras de la A a la Z y números';
}

/** Código de norma comparable: mayúsculas y sin espacios ("iso 9001" → "ISO9001"),
 *  la misma regla del API, para que una norma no se duplique por cómo se escribió. */
export function normalizeNormCode(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase();
}

export function requiredTextError(raw: string, max: number, emptyMessage: string): string | null {
  const value = raw.trim();
  if (value === '') {
    return emptyMessage;
  }
  return value.length > max ? `Admite hasta ${max} caracteres` : null;
}

export function frequencyError(raw: string): string | null {
  const value = raw.trim();
  const days = Number(value);
  const valid = /^\d+$/.test(value) && days >= 1 && days <= FREQUENCY_MAX;
  return valid ? null : `Escribe un número entero de días entre 1 y ${FREQUENCY_MAX}`;
}

export interface CatalogErrorView {
  /** Campo del formulario al que pertenece el error; null = error general. */
  field: 'code' | 'name' | null;
  message: string;
}

const DUPLICATE_CODE: Record<CatalogEntity, string> = {
  document_type: 'Ya existe un tipo de documento con ese fragmento. Usa otro.',
  process_area: 'Ya existe un proceso con ese fragmento en el mismo nivel. Usa otro.',
  norm: 'Esa cláusula ya existe en la norma.',
  custom_field: 'Ya existe un campo con ese nombre. Usa otro.',
};

const DUPLICATE_NAME: Record<CatalogEntity, string> = {
  document_type: 'Ya existe un tipo de documento con ese nombre. Usa otro.',
  process_area: 'Ya existe un proceso con ese nombre. Usa otro.',
  norm: 'Ya existe una cláusula con ese requisito.',
  custom_field: 'Ya existe un campo con ese nombre. Usa otro.',
};

const FORM_REJECTIONS: Record<string, string> = {
  process_area_cycle: 'Un proceso no puede quedar dentro de sí mismo ni de uno de sus subprocesos.',
  cross_company_parent: 'El proceso padre ya no está disponible. Recarga la página y elige otro.',
  options_removed:
    'Las opciones existentes no se pueden quitar. Recarga la página y vuelve a intentar.',
  options_not_select: 'Solo un campo de tipo lista admite opciones.',
  // G3: tipo de proceso y plantilla por tipo de documento.
  process_type_child:
    'El tipo de proceso se define en el proceso principal; el subproceso lo hereda.',
  template_not_current: 'La plantilla debe ser un documento vigente. Elige otra.',
  template_without_lms:
    'La plantilla debe tener su archivo vinculado en el LMS. Elige otra o vincúlalo desde su ficha.',
  template_not_found: 'La plantilla ya no está disponible en la empresa. Elige otra.',
};

/** Rechazo del API → mensaje y campo donde mostrarlo. */
export function catalogError(err: unknown, entity: CatalogEntity): CatalogErrorView {
  const reason = apiErrorReason(err);
  if (reason === 'duplicate_code') {
    return { field: 'code', message: DUPLICATE_CODE[entity] };
  }
  if (reason === 'duplicate_name') {
    return { field: 'name', message: DUPLICATE_NAME[entity] };
  }
  if (reason !== undefined && FORM_REJECTIONS[reason] !== undefined) {
    return { field: null, message: FORM_REJECTIONS[reason] };
  }
  if (err instanceof ApiError && err.status === 404) {
    return {
      field: null,
      message: 'Este elemento ya no está disponible en la empresa. Recarga la página.',
    };
  }
  if (err instanceof ApiError && err.status === 403) {
    return {
      field: null,
      message:
        'No tienes permiso para administrar catálogos. Pide acceso al Administrador de Calidad.',
    };
  }
  return { field: null, message: `No se pudo guardar. ${errorAdvice(err)}` };
}

export interface AreaTreeRow {
  area: ProcessArea;
  depth: number;
  /** Ruta de fragmentos desde la raíz: "PD › AA". */
  path: string;
}

const byFragment = (a: ProcessArea, b: ProcessArea): number =>
  a.codeFragment.localeCompare(b.codeFragment, 'es', { numeric: true });

/**
 * Áreas en orden de árbol (padre seguido de sus hijos, hermanos por fragmento). Un
 * área cuyo padre no vino en la lista se trata como raíz; un ciclo en los datos se
 * corta sin colgarse (cada área se emite una sola vez).
 */
export function areaTree(areas: readonly ProcessArea[]): AreaTreeRow[] {
  const ids = new Set(areas.map((a) => a.id));
  const children = new Map<string | null, ProcessArea[]>();
  for (const a of areas) {
    const parent = a.parentId !== null && ids.has(a.parentId) ? a.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), a]);
  }
  const out: AreaTreeRow[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number, prefix: string): void => {
    for (const a of [...(children.get(parent) ?? [])].sort(byFragment)) {
      if (seen.has(a.id)) {
        continue;
      }
      seen.add(a.id);
      const path = prefix === '' ? a.codeFragment : `${prefix} › ${a.codeFragment}`;
      out.push({ area: a, depth, path });
      walk(a.id, depth + 1, path);
    }
  };
  walk(null, 0, '');
  // Áreas atrapadas en un ciclo (ninguna llega desde una raíz): se muestran igual.
  for (const a of [...areas].sort(byFragment)) {
    if (!seen.has(a.id)) {
      seen.add(a.id);
      out.push({ area: a, depth: 0, path: a.codeFragment });
    }
  }
  return out;
}

/** El área y todos sus descendientes: no pueden ser su nuevo padre. */
export function descendantIds(areas: readonly ProcessArea[], rootId: string): Set<string> {
  const out = new Set<string>([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const a of areas) {
      if (a.parentId !== null && out.has(a.parentId) && !out.has(a.id)) {
        out.add(a.id);
        grew = true;
      }
    }
  }
  return out;
}
